import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  FormHelperText,
  CircularProgress,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { Certificate } from '../../../types/certificate';
import { certificateService } from '../../../services/certificateService';

interface CompanyOption {
  id: number;
  name: string;
}

interface CertificateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (certificate: Certificate) => void;
  companies: CompanyOption[];
}

interface FormErrors {
  company?: string;
  file?: string;
  password?: string;
}

export const CertificateModal: React.FC<CertificateModalProps> = ({
  open,
  onClose,
  onSuccess,
  companies,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [successInfo, setSuccessInfo] = useState<Certificate | null>(null);

  const resetForm = () => {
    setSelectedCompany(null);
    setFile(null);
    setFileName('');
    setPassword('');
    setShowPassword(false);
    setErrors({});
    setSuccessInfo(null);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      const ext = selected.name.toLowerCase().split('.').pop();
      if (ext !== 'pfx' && ext !== 'p12') {
        setErrors((prev) => ({ ...prev, file: 'Formato inválido. Aceitos: .pfx, .p12' }));
        return;
      }
      setFile(selected);
      setFileName(selected.name);
      setErrors((prev) => ({ ...prev, file: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!selectedCompany) newErrors.company = 'Empresa é obrigatória';
    if (!file) newErrors.file = 'Arquivo do certificado é obrigatório';
    if (!password) newErrors.password = 'Senha é obrigatória';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const handleSubmit = async () => {
    if (!validate() || !selectedCompany || !file) return;

    setLoading(true);
    try {
      const result = await certificateService.upload(selectedCompany.id, file, password);
      setSuccessInfo(result);
      onSuccess(result);
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message
        ?? (err instanceof Error ? err.message : 'Erro ao enviar certificado');
      setErrors({ file: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Upload de Certificado Digital</Typography>
          <IconButton onClick={handleClose} disabled={loading} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {successInfo && (
            <Alert severity="success">
              Certificado importado com sucesso!
              <br />
              CNPJ: {successInfo.cnpj ?? '-'} | Emissor: {successInfo.issuer ?? '-'}
              <br />
              Válido de {formatDate(successInfo.validFrom ?? '')} até {formatDate(successInfo.validTo ?? '')}
            </Alert>
          )}

          {!successInfo && (
            <>
              {/* Company */}
              <Autocomplete
                options={companies}
                getOptionLabel={(option) => option.name}
                value={selectedCompany}
                onChange={(_e, value) => {
                  setSelectedCompany(value);
                  setErrors((prev) => ({ ...prev, company: undefined }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Empresa"
                    error={!!errors.company}
                    helperText={errors.company}
                    disabled={loading}
                  />
                )}
                disabled={loading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />

              {/* File upload */}
              <Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pfx,.p12"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  fullWidth
                  sx={{
                    height: 56,
                    borderStyle: errors.file ? 'solid' : 'dashed',
                    borderColor: errors.file ? 'error.main' : 'grey.400',
                  }}
                >
                  {fileName || 'Selecionar certificado (.pfx ou .p12)'}
                </Button>
                {errors.file && (
                  <FormHelperText error>{errors.file}</FormHelperText>
                )}
              </Box>

              {/* Password */}
              <TextField
                label="Senha do Certificado"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, password: undefined }));
                }}
                error={!!errors.password}
                helperText={errors.password}
                disabled={loading}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {successInfo ? 'Fechar' : 'Cancelar'}
        </Button>
        {!successInfo && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            Enviar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CertificateModal;
